/* Shop The Look — shared behavior for the singular section and the grid section.
   <shop-the-look>       one look: dots, popovers, side-list highlight, AJAX add-to-cart, analytics.
   <shop-the-look-grid>  the looks grid: slider arrows + overflow state.
   Attributes on <shop-the-look>:
     data-trigger         'click' | 'hover'
     data-analytics-path  app proxy path ('' disables tracking)
     data-look-handle     metaobject handle for analytics payloads */

(function () {
  'use strict';

  if (!customElements.get('shop-the-look')) {
    class ShopTheLook extends HTMLElement {
      connectedCallback() {
        // Re-init cleanly after the theme editor moves/reloads the section
        this._ac = new AbortController();
        var sig = { signal: this._ac.signal };
        var root = this;

        var banner = root.querySelector('.stl__banner');
        var popEnabled = !!(banner && banner.querySelector('.stl__pop'));
        // On touch devices a tap fires mouseenter before click; opening the card on
        // mouseenter makes the follow-up click land on the add-to-cart button under
        // the finger. Only honor the hover trigger where hovering actually exists.
        var canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
        var hoverTrigger = root.dataset.trigger === 'hover' && canHover;

        var trackEndpoint = root.dataset.analyticsPath || '';
        var lookHandle = root.dataset.lookHandle || '';

        function track(eventName, productGid) {
          if (!trackEndpoint || !lookHandle || !navigator.sendBeacon) return;
          try {
            navigator.sendBeacon(
              trackEndpoint.replace(/\/$/, '') + '/stl/event',
              new Blob(
                [JSON.stringify({ look: lookHandle, event: eventName, product_gid: productGid || null })],
                { type: 'application/json' }
              )
            );
          } catch (e) { /* never break the page for analytics */ }
        }

        track('view');

        root.querySelectorAll('.stl__card-category').forEach(function (link) {
          link.addEventListener('click', function () {
            track('category_click', link.getAttribute('data-stl-product'));
          }, sig);
        });

        /* ---- Popover helpers ---- */
        var openPop = null, openDot = null, closeTimer = null;

        function isStacked() { return window.matchMedia('(max-width: 749px)').matches; }

        // Popovers are clamped inside the nearest boundary box: the grid's
        // scroll track (which clips overflow) when inside the grid section,
        // otherwise the banner itself.
        function boundaryRect() {
          var b = root.closest('[data-stl-boundary]') || banner;
          return b.getBoundingClientRect();
        }

        function closePop() {
          if (banner) {
            banner.querySelectorAll('.stl__pop--open').forEach(function (p) {
              p.classList.remove('stl__pop--open', 'stl__pop--below');
              p.setAttribute('aria-hidden', 'true');
              p.style.removeProperty('--stl-shift');
              p.style.removeProperty('--stl-shift-y');
              p.style.removeProperty('--stl-caret-x');
            });
          }
          root.querySelectorAll('.stl__dot[aria-expanded="true"]').forEach(function (d) {
            d.setAttribute('aria-expanded', 'false');
          });
          openPop = null; openDot = null;
        }

        function positionPop(pop, dot) {
          pop.classList.remove('stl__pop--below');
          pop.style.setProperty('--stl-shift', '0px');
          pop.style.setProperty('--stl-shift-y', '0px');

          var b = boundaryRect();
          var d = dot.getBoundingClientRect();
          var p = pop.getBoundingClientRect();
          var pad = 8;

          // Flip below when the card would overflow the top of the boundary
          if (p.top < b.top + pad) { pop.classList.add('stl__pop--below'); p = pop.getBoundingClientRect(); }

          // Clamp vertically inside the boundary (grid cells are short)
          var shiftY = 0;
          if (p.bottom > b.bottom - pad) shiftY -= p.bottom - (b.bottom - pad);
          if (p.top + shiftY < b.top + pad) shiftY += (b.top + pad) - (p.top + shiftY);
          if (shiftY) { pop.style.setProperty('--stl-shift-y', shiftY + 'px'); p = pop.getBoundingClientRect(); }

          // Clamp horizontally inside the boundary
          var shift = 0;
          if (p.left < b.left + pad) shift += (b.left + pad) - p.left;
          if (p.right > b.right - pad) shift -= p.right - (b.right - pad);
          if (shift) { pop.style.setProperty('--stl-shift', shift + 'px'); p = pop.getBoundingClientRect(); }

          // Keep the caret pointing at the dot
          var dotCenter = d.left + d.width / 2;
          var caretX = Math.max(14, Math.min(p.width - 14, dotCenter - p.left));
          pop.style.setProperty('--stl-caret-x', caretX + 'px');
        }

        function openPopFor(number, dot) {
          closePop();
          if (!banner) return;
          var pop = banner.querySelector('.stl__pop[data-stl-pop="' + number + '"]');
          if (!pop) return;
          pop.classList.add('stl__pop--open');
          pop.setAttribute('aria-hidden', 'false');
          if (dot) dot.setAttribute('aria-expanded', 'true');
          openPop = pop; openDot = dot;
          requestAnimationFrame(function () { positionPop(pop, dot); });
        }

        function scheduleClose() { clearTimeout(closeTimer); closeTimer = setTimeout(closePop, 180); }
        function cancelClose() { clearTimeout(closeTimer); }

        // Dot interaction: highlight the side card, and (optionally) toggle the popover
        root.querySelectorAll('.stl__dot').forEach(function (dot) {
          dot.addEventListener('click', function () {
            track('dot_click', dot.getAttribute('data-stl-product'));
            var number = dot.getAttribute('data-stl-number');

            var card = root.querySelector('[data-stl-card="' + number + '"]');
            root.querySelectorAll('.stl__card--active').forEach(function (el) { el.classList.remove('stl__card--active'); });
            root.querySelectorAll('.stl__dot--active').forEach(function (el) { el.classList.remove('stl__dot--active'); });
            if (card) card.classList.add('stl__card--active');
            dot.classList.add('stl__dot--active');

            if (popEnabled) {
              var pop = banner && banner.querySelector('.stl__pop[data-stl-pop="' + number + '"]');
              if (pop && pop.classList.contains('stl__pop--open')) { closePop(); }
              else { openPopFor(number, dot); }
            } else if (card) {
              card.scrollIntoView({ behavior: 'smooth', block: isStacked() ? 'center' : 'nearest' });
            }
          }, sig);

          if (popEnabled && hoverTrigger) {
            dot.addEventListener('mouseenter', function () {
              if (isStacked()) return;
              cancelClose();
              root.querySelectorAll('.stl__dot--active').forEach(function (el) { el.classList.remove('stl__dot--active'); });
              dot.classList.add('stl__dot--active');
              openPopFor(dot.getAttribute('data-stl-number'), dot);
            }, sig);
            dot.addEventListener('mouseleave', scheduleClose, sig);
          }
        });

        // Popover-level bindings (close button, hover-keepalive, outside click, Esc)
        if (popEnabled && banner) {
          banner.querySelectorAll('.stl__pop').forEach(function (pop) {
            var close = pop.querySelector('.stl__pop-close');
            if (close) close.addEventListener('click', function (e) { e.stopPropagation(); closePop(); }, sig);
            if (hoverTrigger) {
              pop.addEventListener('mouseenter', cancelClose, sig);
              pop.addEventListener('mouseleave', scheduleClose, sig);
            }
          });

          document.addEventListener('click', function (e) {
            if (!openPop) return;
            if (e.target.closest('.stl__pop') || e.target.closest('.stl__dot')) return;
            closePop();
          }, sig);
          document.addEventListener('keydown', function (e) {
            if (e.key === 'Escape' && openPop) closePop();
          }, sig);
          window.addEventListener('resize', function () {
            if (openPop && openDot) positionPop(openPop, openDot);
          }, sig);
          // Sliding the grid moves the dot under an open popover — close it
          var boundary = root.closest('[data-stl-boundary]');
          if (boundary) boundary.addEventListener('scroll', function () { if (openPop) closePop(); }, sig);
        }

        // Add to cart via AJAX (binds to every .stl__atc — list cards and popovers alike)
        var cartElement = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

        root.querySelectorAll('.stl__atc').forEach(function (button) {
          button.addEventListener('click', function () {
            var variantId = button.getAttribute('data-stl-variant');
            if (!variantId || button.disabled) return;

            button.disabled = true;

            var body = { id: parseInt(variantId, 10), quantity: 1 };
            if (cartElement && typeof cartElement.getSectionsToRender === 'function') {
              body.sections = cartElement.getSectionsToRender().map(function (section) { return section.id; });
              body.sections_url = window.location.pathname;
            }

            fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(body)
            })
              .then(function (response) {
                if (!response.ok) throw new Error('add-to-cart failed');
                return response.json();
              })
              .then(function (parsedState) {
                track('add_to_cart', button.getAttribute('data-stl-product'));
                button.classList.add('stl__atc--added');
                var isIconButton = button.classList.contains('stl__pop-atc');
                var original = button.innerHTML;
                button.innerHTML = isIconButton ? '✓' : 'נוסף לסל ✓';
                setTimeout(function () {
                  button.classList.remove('stl__atc--added');
                  button.innerHTML = original;
                  button.disabled = false;
                }, 2000);

                if (cartElement && typeof cartElement.renderContents === 'function') {
                  if (cartElement.classList.contains('is-empty')) cartElement.classList.remove('is-empty');
                  cartElement.renderContents(parsedState);
                } else {
                  document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
                  if (typeof window.updateCartCount === 'function') window.updateCartCount();
                }
              })
              .catch(function () {
                button.disabled = false;
                alert('לא ניתן להוסיף את המוצר לסל, נסו שוב');
              });
          }, sig);
        });
      }

      disconnectedCallback() {
        if (this._ac) this._ac.abort();
      }
    }
    customElements.define('shop-the-look', ShopTheLook);
  }

  if (!customElements.get('shop-the-look-grid')) {
    class ShopTheLookGrid extends HTMLElement {
      connectedCallback() {
        this._ac = new AbortController();
        var sig = { signal: this._ac.signal };
        var root = this;
        var track = root.querySelector('.stl-grid__track');
        var prev = root.querySelector('[data-stl-prev]');
        var next = root.querySelector('[data-stl-next]');
        var thumb = root.querySelector('[data-stl-thumb]');
        if (!track) return;

        // In RTL scrollLeft runs 0 → -max; "next" (more content) scrolls negative.
        function dirSign() {
          return getComputedStyle(track).direction === 'rtl' ? -1 : 1;
        }
        function step() { return Math.max(track.clientWidth * 0.8, 200); }

        function updateArrows() {
          var max = track.scrollWidth - track.clientWidth;
          var pos = Math.abs(track.scrollLeft);
          root.classList.toggle('stl-grid--no-overflow', max <= 1);
          if (prev) prev.disabled = pos <= 1;
          if (next) next.disabled = pos >= max - 1;
          if (thumb && track.scrollWidth > 0) {
            thumb.style.width = (track.clientWidth / track.scrollWidth * 100) + '%';
            thumb.style.insetInlineStart = (pos / track.scrollWidth * 100) + '%';
          }
        }

        // Smooth scrollBy past the edge is silently cancelled when scroll-snap
        // is active (Chromium), so clamp the target and use scrollTo.
        function scrollStep(dirn) {
          var max = track.scrollWidth - track.clientWidth;
          var sign = dirSign();
          var target = track.scrollLeft + dirn * step() * sign;
          if (sign < 0) target = Math.max(-max, Math.min(0, target));
          else target = Math.min(max, Math.max(0, target));
          var from = track.scrollLeft;
          track.scrollTo({ left: target, behavior: 'smooth' });
          // A busy main thread (or the snap quirk) can drop the smooth
          // animation without scrolling — jump instantly if nothing moved.
          setTimeout(function () {
            if (track.scrollLeft === from && from !== target) track.scrollTo({ left: target });
          }, 250);
        }
        if (prev) prev.addEventListener('click', function () { scrollStep(-1); }, sig);
        if (next) next.addEventListener('click', function () { scrollStep(1); }, sig);

        // Draggable scrollbar: drag the thumb to scrub the track, or press the
        // bar to jump there (the drag then continues from that point).
        var bar = root.querySelector('.stl-grid__scrollbar');
        if (bar) {
          bar.addEventListener('pointerdown', function (e) {
            e.preventDefault();
            var rtl = dirSign() < 0;
            var barRect = bar.getBoundingClientRect();
            if (barRect.width <= 0) return;
            var ratio = track.scrollWidth / barRect.width;
            var max = track.scrollWidth - track.clientWidth;

            if (!e.target.closest('.stl-grid__scrollbar-thumb')) {
              var frac = rtl ? (barRect.right - e.clientX) / barRect.width : (e.clientX - barRect.left) / barRect.width;
              var pos = Math.max(0, Math.min(max, frac * track.scrollWidth - track.clientWidth / 2));
              track.scrollLeft = rtl ? -pos : pos;
            }

            var startX = e.clientX, startScroll = track.scrollLeft;
            bar.classList.add('stl-grid__scrollbar--dragging');
            track.classList.add('stl-grid__track--dragging');
            try { bar.setPointerCapture(e.pointerId); } catch (err) { /* synthetic events */ }

            function move(ev) {
              var target = startScroll + (ev.clientX - startX) * ratio;
              if (rtl) target = Math.max(-max, Math.min(0, target));
              else target = Math.min(max, Math.max(0, target));
              track.scrollLeft = target;
            }
            function up(ev) {
              bar.classList.remove('stl-grid__scrollbar--dragging');
              track.classList.remove('stl-grid__track--dragging');
              try { bar.releasePointerCapture(ev.pointerId); } catch (err) { /* synthetic events */ }
              bar.removeEventListener('pointermove', move);
              bar.removeEventListener('pointerup', up);
              bar.removeEventListener('pointercancel', up);
            }
            bar.addEventListener('pointermove', move);
            bar.addEventListener('pointerup', up);
            bar.addEventListener('pointercancel', up);
          }, sig);
        }

        // Theme editor: selecting a block in the sidebar slides its cell into view
        if (window.Shopify && window.Shopify.designMode) {
          document.addEventListener('shopify:block:select', function (e) {
            if (!e.target || !track.contains(e.target)) return;
            e.target.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
          }, sig);
        }

        var ticking = false;
        track.addEventListener('scroll', function () {
          if (ticking) return;
          ticking = true;
          requestAnimationFrame(function () { ticking = false; updateArrows(); });
        }, sig);
        window.addEventListener('resize', updateArrows, sig);
        updateArrows();
      }

      disconnectedCallback() {
        if (this._ac) this._ac.abort();
      }
    }
    customElements.define('shop-the-look-grid', ShopTheLookGrid);
  }
})();
